'use client';
import { createContext, useContext, useEffect, useState } from 'react';

type EventsType = {
	lastFifteenSeconds: Date | null;
	lastThirtySeconds: Date | null;
	lastMinuteTick: Date | null;
	lastTenMinuteTick: Date | null;
	isTabFocused: boolean;
};

const Events = createContext<EventsType>({
	lastFifteenSeconds: null,
	lastThirtySeconds: null,
	lastMinuteTick: null,
	lastTenMinuteTick: null,
	isTabFocused: true,
});

export function EventsProvider({ children }: { children: React.ReactNode }) {
	const [lastFifteenSeconds, setLastFifteenSeconds] = useState<Date | null>(null);
	const [lastThirtySeconds, setLastThirtySeconds] = useState<Date | null>(null);
	const [lastMinuteTick, setLastMinuteTick] = useState<Date | null>(null);
	const [lastTenMinuteTick, setLastTenMinuteTick] = useState<Date | null>(null);
	const [isTabFocused, setIsTabFocused] = useState<boolean>(true);

	useEffect(() => {
		const worker = new Worker(new URL('./timer-worker.js', import.meta.url));

		worker.onmessage = (e) => {
			if (e.data.type === 'lastFifteenSeconds') {
				setLastFifteenSeconds(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastFifteenSeconds', { detail: e.data.now }));
			}
			if (e.data.type === 'lastThirtySeconds') {
				setLastThirtySeconds(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastThirtySeconds', { detail: e.data.now }));
			}
			if (e.data.type === 'lastMinuteTick') {
				setLastMinuteTick(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastMinuteTick', { detail: e.data.now }));
			}
			if (e.data.type === 'lastTenMinuteTick') {
				setLastTenMinuteTick(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastTenMinuteTick', { detail: e.data.now }));
			}
		};

		const notifyFocus = () => {
			const focused = document.hasFocus();
			setIsTabFocused(focused);
			worker.postMessage({ type: 'focus', focused });
		};

		// visibilitychange: handles tab switching
		document.addEventListener('visibilitychange', notifyFocus);
		// blur/focus on both window and document:
		// - window catches minimize and click outside
		// - document catches keyboard app switching (e.g. cmd+tab on Mac)
		window.addEventListener('focus', notifyFocus);
		window.addEventListener('blur', notifyFocus);
		document.addEventListener('focus', notifyFocus);
		document.addEventListener('blur', notifyFocus);

		// sync initial focus state
		notifyFocus();

		return () => {
			worker.terminate();
			document.removeEventListener('visibilitychange', notifyFocus);
			window.removeEventListener('focus', notifyFocus);
			window.removeEventListener('blur', notifyFocus);
			document.removeEventListener('focus', notifyFocus);
			document.removeEventListener('blur', notifyFocus);
		};
	}, []);

	return <Events.Provider value={{ lastFifteenSeconds, lastThirtySeconds, lastMinuteTick, lastTenMinuteTick, isTabFocused }}>{children}</Events.Provider>;
}

export function useEvents() {
	return useContext(Events);
}
